import { useEffect, useRef, useState } from "react";
import { withClientContext } from "~/apollo/client";
import {
  useGetDdqGenerationDefaultsQuery,
  useGetDdqGenerationRunsQuery,
  useRunDdqGenerationMutation,
} from "~/graphql/supergraphHooks";
import type {
  GetDdqGenerationDefaultsQuery,
  ProcessingStatus,
} from "~/graphql/supergraphTypes";

export type GenerationDefaults = GetDdqGenerationDefaultsQuery["ddqGenerationDefaults"];

export const RUN_POLL_INTERVAL_MS = 3000;

export const GENERIC_FAILURE_MESSAGE =
  "JunieAI couldn't draft answers for this DDQ. Try again.";

// Codegen types this field against a string union, not the supergraphEnums enum of the same name.
const IN_FLIGHT: ProcessingStatus[] = ["pending", "in_progress"];

// A run's scope is exactly one of the whole DDQ or one section — no selection UI exists yet for
// individual questions.
export type RunScope =
  { kind: "whole" } | { kind: "section"; sectionId: string; sectionTitle: string };

export type UseGenerateDraftRunResult = {
  defaults: GenerationDefaults | undefined;
  isLoadingDefaults: boolean;
  isRunStateKnown: boolean;
  isRunning: boolean;
  hasEverRun: boolean;
  failureMessage: string | null;
  startError: string | null;
  isStarting: boolean;
  latestSucceededAt: string | null;
  run: (prompt: string, scope: RunScope) => void;
};

export type UseGenerateDraftRunArgs = {
  // Undefined until the wizard's queue row loads — this hook lives at the wizard level (shared
  // by the Structure step's "Generate first draft" trigger and the Generate-first-draft step's
  // re-run triggers), above the point where the row is known to exist.
  arenaId: number | undefined;
  ddqProdOpsTaskId: string | undefined;
  onStarted: () => void;
  onSucceeded: () => void;
};

// Run state is read back off the server rather than remembered locally, so a reload or a
// trip out of the wizard mid-run still lands on the loading state instead of the bar.
export const useGenerateDraftRun = ({
  arenaId,
  ddqProdOpsTaskId,
  onStarted,
  onSucceeded,
}: UseGenerateDraftRunArgs): UseGenerateDraftRunResult => {
  const [startError, setStartError] = useState<string | null>(null);
  const isReady = arenaId != null && ddqProdOpsTaskId != null;

  const { data: defaultsData, loading: isLoadingDefaults } = useGetDdqGenerationDefaultsQuery({
    context: withClientContext("federation"),
  });

  const {
    data: runsData,
    refetch: refetchRuns,
    startPolling,
    stopPolling,
  } = useGetDdqGenerationRunsQuery({
    context: withClientContext("federation"),
    variables: { arenaId: arenaId ?? 0, ddqProdOpsTaskId: ddqProdOpsTaskId ?? "", limit: 1 },
    skip: !isReady,
    fetchPolicy: "cache-and-network",
  });

  const [latestRun] = runsData?.ddqGenerationRuns ?? [];
  const isRunning = latestRun != null && IN_FLIGHT.includes(latestRun.status);
  // Before the first read lands, a run may already be in flight — so "not running" is not yet a
  // fact, and the bar must not offer to start one.
  const isRunStateKnown = runsData != null;

  useEffect(() => {
    if (!isRunning) return;
    startPolling(RUN_POLL_INTERVAL_MS);
    return stopPolling;
  }, [isRunning, startPolling, stopPolling]);

  // Starts undefined so mounting straight into an already-succeeded run doesn't look like a transition.
  const previousStatusRef = useRef<ProcessingStatus | undefined>(undefined);
  useEffect(() => {
    const previousStatus = previousStatusRef.current;
    previousStatusRef.current = latestRun?.status;
    const wasInFlight = previousStatus != null && IN_FLIGHT.includes(previousStatus);
    if (wasInFlight && latestRun?.status === "succeeded") {
      onSucceeded();
    }
  }, [latestRun?.status, onSucceeded]);

  const [runGeneration, { loading: isStarting }] = useRunDdqGenerationMutation({
    context: withClientContext("federation"),
    // The response's source-shaped counts move with the run.
    refetchQueries: ["GetDdqProdOpsQueue"],
  });

  // model/knowledgeSource are left off: Phase 1 only exposes the prompt and the run's scope,
  // so the server default applies to everything else.
  const run = (prompt: string, scope: RunScope) => {
    if (arenaId == null || ddqProdOpsTaskId == null) return;
    setStartError(null);
    runGeneration({
      variables: {
        input: {
          arenaId,
          ddqProdOpsTaskId,
          prompt,
          sectionIds: scope.kind === "section" ? [scope.sectionId] : undefined,
        },
      },
    })
      .then(async () => {
        // Refetch before onStarted: closing the modal first hands back an enabled bar for the
        // length of a round trip, which is long enough to submit a second run.
        await refetchRuns();
        onStarted();
      })
      .catch((error: unknown) => {
        setStartError(error instanceof Error ? error.message : "Something went wrong.");
      });
  };

  return {
    defaults: defaultsData?.ddqGenerationDefaults,
    isLoadingDefaults,
    isRunStateKnown,
    isRunning,
    // Distinct from isRunning: a task can have a run on record (succeeded or failed) with
    // nothing in flight right now. Drives the "Generate first draft" vs. "Re-run AI job" label —
    // the button reused for both first-run and every subsequent run.
    hasEverRun: latestRun != null,
    failureMessage:
      latestRun?.status === "failed" ? (latestRun.error ?? GENERIC_FAILURE_MESSAGE) : null,
    startError,
    isStarting,
    latestSucceededAt:
      latestRun?.status === "succeeded" ? (latestRun.succeededAt ?? null) : null,
    run,
  };
};
